import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { Mail, Phone, MapPin, Send, MessageCircle, Clock } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INBOX_EMAIL = "hello@tutorsmelbourne.com.au";

const SUBJECTS = [
  "General enquiry",
  "Book a class",
  "Tutor availability",
  "Pricing & packages",
  "Become a tutor",
  "Feedback / other",
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [loading, setLoading] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Thanks — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="contact-page">
      <Seo
        title="Contact Us | Small Group English Classes Melbourne"
        rawTitle
        description="Talk to Tutors Melbourne about small group VCE English and EAL classes. Call 0468 320 157 or send an enquiry — we reply to most within 24 hours."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Tutors Melbourne",
          mainEntity: {
            "@type": "EducationalOrganization",
            name: "Tutors Melbourne",
            telephone: "+61468320157",
            email: INBOX_EMAIL,
            areaServed: { "@type": "City", name: "Melbourne" },
          },
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="hero-gradient dark:bg-transparent pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
          <Badge className="bg-brand-purple-soft text-brand-purple mb-4 font-body font-semibold px-4 py-1.5 text-sm rounded-full" data-testid="contact-badge">
            Get in Touch
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight mb-6">
            We'd love to hear from you
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 font-body leading-relaxed max-w-2xl mx-auto">
            Got a question about a class, need help picking the right tutor, or just want to say hi? Send us a message and we'll get back to you fast.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 bg-white dark:bg-[#0f1117]" data-testid="contact-content">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Info cards */}
          <div className="lg:col-span-2 space-y-4">
            <a
              href={`mailto:${INBOX_EMAIL}`}
              className="block bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6 card-hover"
              data-testid="contact-email-card"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-purple-soft dark:bg-brand-purple/20 flex items-center justify-center mb-4">
                <Mail className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />
              </div>
              <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white mb-1">Email us</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-body mb-2">Fastest way to reach us</p>
              <p className="text-sm font-body font-semibold text-brand-purple dark:text-brand-purple-light break-all">{INBOX_EMAIL}</p>
            </a>

            <a href={PHONE_HREF} className="block bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6 hover:border-brand-purple/40 transition-colors" data-testid="contact-phone-card">
              <div className="w-11 h-11 rounded-xl bg-brand-purple-soft dark:bg-brand-purple/20 flex items-center justify-center mb-4">
                <Phone className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />
              </div>
              <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white mb-1">Call us</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-body mb-2">Happy to talk it through</p>
              <p className="text-sm font-body font-semibold text-brand-purple dark:text-brand-purple-light">{PHONE_DISPLAY}</p>
            </a>

            <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6" data-testid="contact-response-card">
              <div className="w-11 h-11 rounded-xl bg-brand-gold/10 dark:bg-brand-gold/20 flex items-center justify-center mb-4">
                <Clock className="h-5 w-5 text-brand-gold" />
              </div>
              <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white mb-1">Response time</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-body">We reply to most enquiries within 24 hours, often much faster during school terms.</p>
            </div>

            <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6" data-testid="contact-location-card">
              <div className="w-11 h-11 rounded-xl bg-brand-purple-soft dark:bg-brand-purple/20 flex items-center justify-center mb-4">
                <MapPin className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />
              </div>
              <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white mb-1">Based in Melbourne</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-body">Hybrid model: online or in-person, across Melbourne.</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="contact-form-card">
              <div className="flex items-center gap-3 mb-6">
                <MessageCircle className="h-6 w-6 text-brand-purple dark:text-brand-purple-light" />
                <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">Send a message</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact-name" className="font-body text-sm text-slate-700 dark:text-slate-200">Full name *</Label>
                    <Input
                      id="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      className="mt-1.5 rounded-xl"
                      required
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="font-body text-sm text-slate-700 dark:text-slate-200">Email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className="mt-1.5 rounded-xl"
                      required
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact-phone" className="font-body text-sm text-slate-700 dark:text-slate-200">Phone (optional)</Label>
                    <Input
                      id="contact-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="04XX XXX XXX"
                      className="mt-1.5 rounded-xl"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-subject" className="font-body text-sm text-slate-700 dark:text-slate-200">Subject</Label>
                    <select
                      id="contact-subject"
                      value={form.subject}
                      onChange={update("subject")}
                      className="mt-1.5 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-purple/50"
                      data-testid="contact-input-subject"
                    >
                      {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact-message" className="font-body text-sm text-slate-700 dark:text-slate-200">Message *</Label>
                  <Textarea
                    id="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us a little about what you're looking for — year level, subjects, goals, timing..."
                    rows={6}
                    className="mt-1.5 rounded-xl resize-none"
                    required
                    data-testid="contact-input-message"
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="rounded-full bg-brand-gold hover:bg-brand-gold-hover text-white font-body font-bold px-8 py-6 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : (
                      <>Send message <Send className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400 dark:text-slate-500 font-body">
                    Or email us directly at{" "}
                    <a href={`mailto:${INBOX_EMAIL}`} className="text-brand-purple dark:text-brand-purple-light font-semibold underline">
                      {INBOX_EMAIL}
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 dark:bg-[#0a0b10] border-t border-slate-100 dark:border-slate-800 py-12 px-6 transition-colors duration-300 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light">Tutors Melbourne</p>
          <p className="text-sm text-brand-gold font-body font-semibold">Learning Perfected</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 font-body mt-4">&copy; {new Date().getFullYear()} Tutors Melbourne. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
